import React, { useEffect, useState } from 'react';
import { Link, useLocation, withRouter } from 'react-router-dom';
import { Col, Layout, Menu, MenuProps, Row } from 'antd';
import {
  DesktopOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  BankOutlined,
  ContactsOutlined,
  LineChartOutlined,
  BookOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ScheduleOutlined,
  TeamOutlined,
  SendOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../modules';
import PageDropdown from './PageDropdown';
import { USER_ROLE_NAME, getLoggedInUserAsync } from '../../modules/auth';
import { getProjectDetailAsync } from '../../modules/projects';

const { Header, Content, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const StyledLayout = styled(Layout)`
  #components-layout-demo-custom-trigger .trigger {
    font-size: 18px;
    line-height: 64px;
    padding: 0 24px;
    cursor: pointer;
    transition: color 0.3s;
  }

  #components-layout-demo-custom-trigger .trigger:hover {
    color: #1890ff;
  }

  #components-layout-demo-custom-trigger .logo {
    height: 32px;
    background: rgba(255, 255, 255, 0.3);
    margin: 16px;
  }

  .site-layout .site-layout-background {
    background: #fff;
  }

  .header-drop-down {
    margin-right: 50px;
  }

  .trigger {
    font-size: 18px;
    line-height: 64px;
    padding: 0 24px;
    cursor: pointer;
    transition: color 0.3s;
  }

  .trigger:hover {
    color: #1890ff;
  }

  .logo {
    height: 32px;
    background: rgba(255, 255, 255, 0.3);
    margin: 16px;
  }

  .site-layout .site-layout-background {
    background: #fff;
  }

  .move-to-main-btn {
    height: 64px;
    width: 40px;
    padding: 0;
  }

  .project-name {
    display: inline;
  }

  .owner-name {
    height: 32px;
    color: #fff;
    margin-top: 20px;
  }
`;

interface SiderNavProps {
  children?: React.ReactNode;
}

function SiderNav({ children }: SiderNavProps) {
  const [mount, setMount] = useState(true);
  const [collapsed, setCollapsed] = useState<any>(false);
  const [items, setItems] = useState<MenuItem[]>();

  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.auth.data);
  const project = useSelector(
    (state: RootState) => state.projects.project.data,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!mount) return;
    if (user === null) {
      dispatch(getLoggedInUserAsync.request(null));
    }
    return () => setMount(false);
  }, [user, dispatch, mount]);

  useEffect(() => {
    if (!mount) return;
    if (project == null && location !== null) {
      const myRe = /^\/enterprises\/(\d+)\/projects\/(\d+)/;
      const from = location.pathname.toString();
      const results: any = from.match(myRe);

      dispatch(
        getProjectDetailAsync.request({
          enterpriseId: results[1],
          projectId: results[2],
        }),
      );
    }
    return () => setMount(false);
  }, [project, dispatch, location, mount]);

  useEffect(() => {
    function getItem(
      label: React.ReactNode,
      key?: React.Key | null,
      icon?: React.ReactNode,
      children?: MenuItem[],
      type?: 'group',
    ): MenuItem {
      return {
        key,
        icon,
        children,
        label,
        type,
      } as MenuItem;
    }

    if (user === null) return;
    if (project === null) return;

    const _items: MenuItem[] = [
      getItem(
        <Link
          to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/dashboard`}
        >
          공사현황
        </Link>,
        '0',
        <DesktopOutlined rev={undefined} />,
      ),
      user.roleName !== USER_ROLE_NAME.STAFF
        ? getItem(
            <Link
              to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/collaborators`}
            >
              공사참여자
            </Link>,
            '1',
            <ContactsOutlined rev={undefined} />,
          )
        : null,
      getItem(
        <Link
          to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/bidPackagesBudgeting`}
        >
          견적요청
        </Link>,
        '2',
        <SendOutlined rev={undefined} />,
      ),
      getItem(
        <Link
          to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/primeContracts`}
        >
          도급계약
        </Link>,
        '3',
        <BankOutlined rev={undefined} />,
      ),
      getItem(
        <Link
          to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/changeOrders/list`}
        >
          설계변경
        </Link>,
        '4',
        <FileTextOutlined rev={undefined} />,
      ),
      getItem('공정계획', '5', <ScheduleOutlined rev={undefined} />, [
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/schedules/list`}
          >
            리스트
          </Link>,
          '5-0',
        ),
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/schedules/chart`}
          >
            공정표
          </Link>,
          '5-1',
        ),
      ]),
      getItem(
        <Link
          to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/tasks`}
        >
          업무
        </Link>,
        '6',
        <TeamOutlined rev={undefined} />,
      ),
      getItem('작업일보', '7', <BarChartOutlined rev={undefined} />, [
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/workRecords/list`}
          >
            리스트
          </Link>,
          '7-0',
        ),
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/workRecords/direct-worker-list`}
          >
            출역현황
          </Link>,
          '7-1',
        ),
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/workRecords/statistics`}
          >
            통계
          </Link>,
          '7-2',
        ),
      ]),
      getItem('비용관리', '8', <LineChartOutlined rev={undefined} />, [
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/expenses`}
          >
            지출
          </Link>,
          '8-0',
        ),
        getItem(
          <Link
            to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/invoices`}
          >
            청구서
          </Link>,
          '8-1',
        ),
      ]),
      user.roleName !== USER_ROLE_NAME.STAFF
        ? getItem(
            <Link
              to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/report`}
            >
              보고서
            </Link>,
            '9',
            <BookOutlined rev={undefined} />,
          )
        : null,
    ];

    setItems(_items);
  }, [user, project]);

  const toggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <>
      <StyledLayout>
        {user && project && (
          <Sider trigger={null} collapsible collapsed={collapsed}>
            <Row justify="center">
              <Col>
                <div className="owner-name">
                  <Link
                    to={`/enterprises/${user.myEnterprise.id}/main`}
                    style={{
                      textDecoration: 'none',
                      height: '32px',
                      color: '#fff',
                      marginTop: '20px',
                    }}
                  >
                    하이공사
                  </Link>
                </div>
              </Col>
            </Row>
            <Menu
              theme="dark"
              mode="inline"
              defaultSelectedKeys={['0']}
              items={items}
            >
              {/* <Menu.Item key="0" icon={<DesktopOutlined rev={undefined} />}>
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/dashboard`}
                >
                  공사현황
                </Link>
              </Menu.Item>
              <Menu.Item
                key="1"
                icon={<ContactsOutlined rev={undefined} />}
                hidden={isHidden}
              >
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/partnerManagement?tab=1`}
                >
                  공사참여자
                </Link>
              </Menu.Item>
              <Menu.Item
                key="2"
                icon={<TeamOutlined rev={undefined} />}
                hidden={isHidden}
              >
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/bidPackagesBudgeting`}
                >
                  견적요청
                </Link>
              </Menu.Item>
              <Menu.Item
                key="4"
                icon={<BankOutlined rev={undefined} />}
                hidden={isHidden}
              >
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/primeContracts`}
                >
                  도급계약
                </Link>
              </Menu.Item>
              <Menu.Item
                key="5"
                icon={<FileTextOutlined rev={undefined} />}
                hidden={isHidden}
              >
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/changeOrders/list`}
                >
                  설계변경
                </Link>
              </Menu.Item>
              <SubMenu
                key="sub3"
                icon={<LineChartOutlined rev={undefined} />}
                title="지출관리"
                data-test={'menu-contract'}
              >
                <Menu.Item key="6">
                  <Link
                    to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/expenses`}
                  >
                    영수증
                  </Link>
                </Menu.Item>
                <Menu.Item key="7">
                  <Link
                    to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/invoices`}
                  >
                    청구서
                  </Link>
                </Menu.Item>
              </SubMenu>
              <Menu.Item key="8" icon={<BookOutlined rev={undefined} />}>
                <Link
                  to={`/enterprises/${user.myEnterprise.id}/projects/${project.id}/report`}
                >
                  보고서
                </Link>
              </Menu.Item> */}
            </Menu>
          </Sider>
        )}
        <StyledLayout className="site-layout">
          {user && (
            <Header className="site-layout-background" style={{ padding: 0 }}>
              {project && (
                <Row justify="space-between">
                  <Col>
                    {React.createElement(
                      collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
                      {
                        className: 'trigger',
                        onClick: toggle,
                        rev: undefined,
                      },
                    )}
                    <h3 className="project-name">{project.name}</h3>
                  </Col>
                  <Col style={{ paddingRight: '50px' }}>
                    <PageDropdown />
                  </Col>
                </Row>
              )}
            </Header>
          )}
          <Content
            className="site-layout-background"
            style={{
              margin: '24px 16px',
              padding: 24,
              minHeight: 280,
            }}
          >
            {children}
          </Content>
        </StyledLayout>
      </StyledLayout>
    </>
  );
}

export default withRouter(SiderNav);
